import type { Phase, Player, RoomState } from '../../shared/types.js';
import type { ServerMsg } from '../../shared/protocol.js';
import { scoreSelection } from '../../shared/scoring.js';
import {
  DEFAULT_TARGET_SCORE,
  MAX_TARGET_SCORE,
  MIN_TARGET_SCORE,
  SINGLE_FIVE,
  SINGLE_ONE,
  STRAIGHT_1_5,
  STRAIGHT_2_6,
  STRAIGHT_FULL,
} from '../../shared/rules.js';
import { Store, type ClientState } from '../store.js';
import { WSClient, clearResumeToken } from '../ws.js';
import { renderDie } from './dice.js';
import { renderLocal } from './local.js';
import { defaultLocalGame } from '../local-game.js';
import { animateCount, celebrate } from './anim.js';
import { makeMuteButton } from './controls.js';
import { flashTitleYourTurn, stopTitleFlash } from '../notify.js';
import {
  playBank, playBust, playHotDice, playRoll, playSelect, playWin, playYourTurn,
} from '../sound.js';

const ERR_LABELS: Record<string, string> = {
  ROOM_NOT_FOUND: 'Raum nicht gefunden.',
  ROOM_FULL: 'Raum ist voll.',
  ALREADY_IN_ROOM: 'Du bist bereits in einem Raum.',
  NOT_HOST: 'Nur der Host darf das.',
  NOT_YOUR_TURN: 'Nicht dein Zug.',
  WRONG_PHASE: 'Aktion gerade nicht erlaubt.',
  INVALID_SELECTION: 'Auswahl ergibt keine gültige Wertung.',
  INVALID_TARGET_SCORE: 'Ungültige Zielpunktzahl.',
  NOT_ENOUGH_PLAYERS: 'Mindestens 2 Spieler nötig.',
  RATE_LIMIT: 'Zu schnell — bitte etwas warten.',
  BAD_MESSAGE: 'Nachricht konnte nicht gelesen werden.',
};

export function mountUi(root: HTMLElement, store: Store, ws: WSClient): void {
  ws.on((msg: ServerMsg) => {
    switch (msg.type) {
      case 'welcome':
        store.update(s => { s.connected = true; s.myPlayerId = msg.playerId || s.myPlayerId; });
        return;
      case 'roomState':
        store.update(s => { s.room = msg.state; });
        return;
      case 'roomLeft':
        store.update(s => { s.room = null; });
        clearResumeToken();
        return;
      case 'error':
        store.update(s => { s.lastError = { code: msg.code, msg: msg.msg, ts: Date.now() }; });
        return;
    }
  });

  // Verbindungsstatus in den Store spiegeln (war vorher tote State).
  ws.onStatus(connected => store.update(s => { s.connected = connected; }));

  root.innerHTML = '';
  root.classList.add('app-root');

  const view = document.createElement('div');
  view.className = 'view';
  root.appendChild(view);

  const toast = document.createElement('div');
  toast.className = 'toast';
  root.appendChild(toast);

  const connBanner = document.createElement('div');
  connBanner.className = 'conn-banner';
  root.appendChild(connBanner);

  store.subscribe(s => {
    applyEffects(s);
    renderView(view, s, store, ws);
    renderToast(toast, s);
    renderConnBanner(connBanner, s);
  });
}

// --- Effekt-Schicht: diffe den Raum-State und löse Sound / Titel-Flash / Konfetti aus ---

type FxSnapshot = {
  roomId: string;
  phase: Phase;
  activePlayerId: string | null;
  scores: Record<string, number>;
  diceSig: string;
  hotDice: boolean;
};
let fxPrev: FxSnapshot | null = null;
let everConnected = false;
const firstSeenAt = Date.now();

function applyEffects(s: ClientState): void {
  const room = s.room;
  if (!room) { fxPrev = null; stopTitleFlash(); return; }

  const scores: Record<string, number> = {};
  for (const p of room.players) scores[p.id] = p.score;
  const diceSig =
    room.dice.map(d => d.value).join(',') + '|' + room.dice.map(d => (d.locked ? '1' : '0')).join('');
  const cur: FxSnapshot = {
    roomId: room.id,
    phase: room.phase,
    activePlayerId: room.activePlayerId,
    scores,
    diceSig,
    hotDice: room.hotDiceAvailable,
  };
  const prev = fxPrev;
  fxPrev = cur;
  if (!prev || prev.roomId !== room.id) return; // erste Beobachtung im Raum → keine Sounds

  const justWon = cur.phase === 'finished' && prev.phase !== 'finished';

  // Wurf-Sound nur bei echten Würfen — nicht beim Reset auf 'waiting' (Rematch).
  if (prev.diceSig !== cur.diceSig && cur.phase !== 'waiting' && cur.phase !== 'finished') playRoll();
  if (prev.phase !== 'between-turns' && cur.phase === 'between-turns') playBust();
  if (cur.hotDice && !prev.hotDice) playHotDice();

  if (justWon) {
    playWin();
    celebrate();
  } else {
    for (const p of room.players) {
      if ((prev.scores[p.id] ?? 0) < p.score) { playBank(); break; }
    }
  }

  const myId = s.myPlayerId;
  const wasMine = prev.activePlayerId === myId;
  const isMine = cur.activePlayerId === myId;
  const running = cur.phase === 'selecting' || cur.phase === 'rolling';
  if (isMine && !wasMine && running) { playYourTurn(); flashTitleYourTurn(); }
  if (!isMine && wasMine) stopTitleFlash();
}

function renderConnBanner(host: HTMLElement, s: ClientState): void {
  if (s.connected) {
    everConnected = true;
    host.classList.remove('show');
    host.textContent = '';
    return;
  }
  // Auf dem allerersten Verbindungsaufbau kein „verloren" zeigen (kurzes Flackern vermeiden).
  if (everConnected) {
    host.textContent = 'Verbindung verloren — verbinde neu …';
    host.classList.add('show');
  } else if (Date.now() - firstSeenAt > 1500) {
    host.textContent = 'Verbinde mit dem Server …';
    host.classList.add('show');
  } else {
    host.classList.remove('show');
  }
}

function renderView(host: HTMLElement, s: ClientState, store: Store, ws: WSClient): void {
  if (s.local) {
    renderLocal(host, store);
    return;
  }
  host.innerHTML = '';
  if (!s.room) {
    host.appendChild(renderLobby(s, store, ws));
    return;
  }
  host.appendChild(renderTable(s, ws));
}

function renderLobby(s: ClientState, store: Store, ws: WSClient): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'lobby';

  const top = document.createElement('div');
  top.className = 'lobby-top';
  top.appendChild(makeMuteButton());
  wrap.appendChild(top);

  const h1 = document.createElement('h1');
  const titleText = document.createElement('span');
  titleText.className = 'title-text';
  titleText.textContent = 'Würfeln';
  h1.appendChild(titleText);
  wrap.appendChild(h1);

  const localCta = document.createElement('button');
  localCta.type = 'button';
  localCta.className = 'local-cta';
  localCta.innerHTML = '<span>Tisch-Spiel</span><small>Echte Würfel — App rechnet die Punkte</small>';
  localCta.addEventListener('click', () => store.update(st => { st.local = defaultLocalGame(); }));
  wrap.appendChild(localCta);

  const nameLabel = document.createElement('label');
  nameLabel.textContent = 'Dein Name';
  const nameInput = document.createElement('input');
  nameInput.type = 'text';
  nameInput.maxLength = 20;
  nameInput.value = s.name;
  nameInput.placeholder = 'z. B. Heinrich';
  // Persist on blur/Enter only — re-rendering on every keystroke kills focus.
  nameInput.addEventListener('change', () => {
    store.update(st => { st.name = nameInput.value; });
    ws.setName(nameInput.value);
  });
  nameLabel.appendChild(nameInput);
  wrap.appendChild(nameLabel);

  const create = document.createElement('section');
  create.className = 'lobby-card';
  const createH = document.createElement('h2');
  createH.textContent = 'Neuen Raum erstellen';
  const targetLabel = document.createElement('label');
  targetLabel.textContent = 'Zielpunktzahl';
  const targetInput = document.createElement('input');
  targetInput.type = 'number';
  targetInput.min = String(MIN_TARGET_SCORE);
  targetInput.max = String(MAX_TARGET_SCORE);
  targetInput.step = '500';
  targetInput.value = String(DEFAULT_TARGET_SCORE);
  targetLabel.appendChild(targetInput);
  const createBtn = document.createElement('button');
  createBtn.type = 'button';
  createBtn.className = 'primary';
  createBtn.textContent = 'Raum erstellen';
  createBtn.addEventListener('click', () => {
    const target = Number(targetInput.value);
    ws.send({ type: 'createRoom', targetScore: target, name: nameInput.value });
  });
  create.append(createH, targetLabel, createBtn);

  const join = document.createElement('section');
  join.className = 'lobby-card';
  const joinH = document.createElement('h2');
  joinH.textContent = 'Beitreten';
  const codeLabel = document.createElement('label');
  codeLabel.textContent = 'Raumcode';
  const codeInput = document.createElement('input');
  codeInput.type = 'text';
  codeInput.maxLength = 4;
  codeInput.placeholder = 'ABCD';
  codeInput.autocapitalize = 'characters';
  codeInput.style.textTransform = 'uppercase';
  codeInput.addEventListener('input', () => {
    codeInput.value = codeInput.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
  });
  const joinBtn = document.createElement('button');
  joinBtn.type = 'button';
  joinBtn.className = 'primary';
  joinBtn.textContent = 'Beitreten';
  const doJoin = (): void => {
    const code = codeInput.value.trim().toUpperCase();
    if (code.length === 4) ws.send({ type: 'joinRoom', roomId: code, name: nameInput.value });
  };
  joinBtn.addEventListener('click', doJoin);
  codeInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') doJoin(); });
  codeLabel.appendChild(codeInput);
  join.append(joinH, codeLabel, joinBtn);

  wrap.append(create, join);
  wrap.appendChild(renderRulesCheatSheet());
  return wrap;
}

function renderTable(s: ClientState, ws: WSClient): HTMLElement {
  const room = s.room!;
  const me = room.players.find(p => p.id === s.myPlayerId) ?? null;
  const isMyTurn = room.activePlayerId === s.myPlayerId;
  const canReorder = !!me?.isHost && (room.phase === 'waiting' || room.phase === 'finished');

  const wrap = document.createElement('div');
  wrap.className = 'table';

  const header = document.createElement('div');
  header.className = 'table-header';

  const code = document.createElement('button');
  code.type = 'button';
  code.className = 'room-code';
  code.title = 'Code kopieren';
  code.innerHTML = `<span class="label">Raum</span><span class="value">${room.id}</span>`;
  code.addEventListener('click', () => copyRoomCode(room.id, code));

  const target = document.createElement('div');
  target.className = 'target-score';
  target.innerHTML = `<span class="label">Ziel</span><span class="value">${room.targetScore}</span>`;

  const controls = document.createElement('div');
  controls.className = 'controls';
  const leave = document.createElement('button');
  leave.type = 'button';
  leave.className = 'ghost';
  leave.textContent = 'Verlassen';
  leave.addEventListener('click', () => ws.send({ type: 'leaveRoom' }));
  controls.append(makeMuteButton(), leave);

  header.append(code, target, controls);
  wrap.appendChild(header);

  wrap.appendChild(renderPlayers(room, canReorder, ws));

  if (room.phase === 'waiting') {
    wrap.appendChild(renderWaitingArea(room, me, ws));
  } else if (room.phase === 'finished') {
    wrap.appendChild(renderFinishedArea(room, me, ws));
  } else {
    wrap.appendChild(renderGameArea(room, isMyTurn, ws));
  }

  wrap.appendChild(renderRulesCheatSheet());
  wrap.appendChild(renderLog(room));
  return wrap;
}

// Score-Count-up: pro Raum den zuletzt gezeigten Score je Spieler merken.
let scoreTrackRoomId: string | null = null;
const lastShownScore = new Map<string, number>();

function renderPlayers(room: RoomState, canReorder: boolean, ws: WSClient): HTMLElement {
  if (room.id !== scoreTrackRoomId) { scoreTrackRoomId = room.id; lastShownScore.clear(); }
  const list = document.createElement('div');
  list.className = 'players';
  room.players.forEach((p, idx) => {
    const card = document.createElement('div');
    card.className = 'player';
    if (p.id === room.activePlayerId) card.classList.add('active');
    if (!p.connected) card.classList.add('offline');
    if (p.isHost) card.classList.add('host');

    const nameDiv = document.createElement('div');
    nameDiv.className = 'player-name';
    nameDiv.innerHTML = `${escapeHtml(p.name)}${p.isHost ? ' <span class="badge">Host</span>' : ''}`;

    const scoreDiv = document.createElement('div');
    scoreDiv.className = 'player-score';
    const prev = lastShownScore.get(p.id);
    if (prev !== undefined && prev !== p.score) {
      scoreDiv.textContent = String(prev);
      animateCount(scoreDiv, prev, p.score);
      if (p.score > prev) scoreDiv.classList.add('score-bump');
    } else {
      scoreDiv.textContent = String(p.score);
    }
    lastShownScore.set(p.id, p.score);

    card.append(nameDiv, scoreDiv);
    if (!p.connected) {
      const st = document.createElement('div');
      st.className = 'player-status';
      st.textContent = 'offline';
      card.appendChild(st);
    }

    if (canReorder) {
      const reorder = document.createElement('div');
      reorder.className = 'player-reorder';
      const up = document.createElement('button');
      up.type = 'button';
      up.className = 'reorder-btn';
      up.textContent = '▲';
      up.title = 'Hoch';
      up.disabled = idx === 0;
      up.addEventListener('click', () => ws.send({ type: 'movePlayer', playerId: p.id, direction: 'up' }));
      const down = document.createElement('button');
      down.type = 'button';
      down.className = 'reorder-btn';
      down.textContent = '▼';
      down.title = 'Runter';
      down.disabled = idx === room.players.length - 1;
      down.addEventListener('click', () => ws.send({ type: 'movePlayer', playerId: p.id, direction: 'down' }));
      reorder.append(up, down);
      card.appendChild(reorder);
    }
    list.appendChild(card);
  });
  return list;
}

function renderWaitingArea(room: RoomState, me: Player | null, ws: WSClient): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'phase-area waiting';
  const msg = document.createElement('p');
  msg.className = 'phase-msg';
  msg.textContent = room.players.length < 2
    ? `Warte auf weitere Spieler — Code: ${room.id}`
    : 'Bereit zum Start.';
  wrap.appendChild(msg);

  if (me?.isHost) {
    const startBtn = document.createElement('button');
    startBtn.type = 'button';
    startBtn.className = 'primary big';
    startBtn.textContent = 'Spiel starten';
    startBtn.disabled = room.players.length < 2;
    startBtn.addEventListener('click', () => ws.send({ type: 'startGame' }));
    wrap.appendChild(startBtn);
  } else {
    const note = document.createElement('p');
    note.className = 'phase-msg muted';
    note.textContent = 'Der Host startet das Spiel.';
    wrap.appendChild(note);
  }
  return wrap;
}

// Track per-room dice values across renders so we can animate just the dice
// that actually changed. Reset when the room id flips.
let lastDiceRoomId: string | null = null;
let lastDiceValues: number[] = [];
let lastDiceLocked: boolean[] = [];

// Track who's up so we play the intro only on the first roll of each turn.
let lastTurnRoomId: string | null = null;
let lastTurnPlayerId: string | null = null;

// Turn-points count-up / pop.
let lastTurnPtsRoom: string | null = null;
let lastTurnPts = 0;

// Preserved across full re-renders (renderView wipes innerHTML each tick).
let rulesOpen = false;
let logScrollTop = 0;

function detectFirstRollOfTurn(room: RoomState): boolean {
  if (room.id !== lastTurnRoomId) {
    lastTurnRoomId = room.id;
    lastTurnPlayerId = room.activePlayerId;
    return false;
  }
  if (room.activePlayerId !== lastTurnPlayerId) {
    lastTurnPlayerId = room.activePlayerId;
    return room.activePlayerId !== null;
  }
  return false;
}

function detectRolledIndices(room: RoomState): Set<number> {
  const animated = new Set<number>();
  if (room.id !== lastDiceRoomId) {
    lastDiceRoomId = room.id;
    lastDiceValues = room.dice.map(d => d.value);
    lastDiceLocked = room.dice.map(d => d.locked);
    return animated;
  }
  room.dice.forEach((d, i) => {
    const prevVal = lastDiceValues[i];
    const prevLocked = lastDiceLocked[i] ?? false;
    const cameOutOfLock = prevLocked && !d.locked;
    const valueChanged = prevVal !== undefined && prevVal !== d.value && !d.locked;
    if (valueChanged || cameOutOfLock) animated.add(i);
    lastDiceValues[i] = d.value;
    lastDiceLocked[i] = d.locked;
  });
  return animated;
}

function renderGameArea(room: RoomState, isMyTurn: boolean, ws: WSClient): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'phase-area playing';

  const turnInfo = document.createElement('div');
  turnInfo.className = 'turn-info';
  const activeName = room.players.find(p => p.id === room.activePlayerId)?.name ?? '?';
  const whoText = isMyTurn ? 'Du bist am Zug' : `${escapeHtml(activeName)} ist am Zug`;
  turnInfo.innerHTML = `
    <div class="turn-line${isMyTurn ? ' my-turn' : ''}">
      <span class="who">${whoText}</span>
      <span class="turn-points">Zug-Punkte: <strong>${room.turnPoints}</strong></span>
    </div>
  `;
  wrap.appendChild(turnInfo);

  // Zug-Punkte hochzählen + Pop, wenn sie gewachsen sind.
  if (room.id !== lastTurnPtsRoom) { lastTurnPtsRoom = room.id; lastTurnPts = room.turnPoints; }
  const tpEl = turnInfo.querySelector('.turn-points strong') as HTMLElement | null;
  if (tpEl) {
    if (lastTurnPts !== room.turnPoints && room.turnPoints > lastTurnPts) {
      tpEl.textContent = String(lastTurnPts);
      animateCount(tpEl, lastTurnPts, room.turnPoints, 450);
      tpEl.classList.add('bumped');
    }
    lastTurnPts = room.turnPoints;
  }

  const isFirstRoll = detectFirstRollOfTurn(room);
  const rolled = detectRolledIndices(room);
  const tray = document.createElement('div');
  tray.className = 'dice-tray';
  room.dice.forEach((d, i) => {
    const animatesNow = isFirstRoll || rolled.has(i);
    const el = renderDie(d, {
      index: i,
      selectable: isMyTurn && room.phase === 'selecting' && !d.locked,
      onClick: isMyTurn ? (idx) => { playSelect(); ws.send({ type: 'toggleDie', index: idx }); } : undefined,
      scramble: animatesNow && !d.locked,
    });
    if (isFirstRoll) {
      el.style.animationDelay = `${i * 90}ms`;
      el.classList.add('intro-rolling');
    } else if (rolled.has(i)) {
      el.style.animationDelay = `${i * 35}ms`;
      el.classList.add('rolling');
    }
    tray.appendChild(el);
  });
  wrap.appendChild(tray);

  const heldValues = room.dice.filter(d => d.held && !d.locked).map(d => d.value);
  const sel = scoreSelection(heldValues);
  const preview = document.createElement('div');
  preview.className = 'selection-preview';
  if (heldValues.length === 0) {
    preview.textContent = 'Wähle wertende Würfel aus.';
  } else if (!sel.valid) {
    preview.classList.add('invalid');
    preview.textContent = 'Auswahl ergibt keine gültige Wertung.';
  } else {
    preview.textContent = `Auswahl: +${sel.points}`;
  }
  wrap.appendChild(preview);

  const actions = document.createElement('div');
  actions.className = 'actions';
  const rollBtn = document.createElement('button');
  rollBtn.type = 'button';
  rollBtn.className = 'primary';
  rollBtn.textContent = 'Auswahl werten & weiterwürfeln';
  rollBtn.disabled = !isMyTurn || room.phase !== 'selecting' || !sel.valid;
  rollBtn.addEventListener('click', () => ws.send({ type: 'roll' }));
  const bankBtn = document.createElement('button');
  bankBtn.type = 'button';
  bankBtn.className = 'secondary';
  bankBtn.textContent = `Banken (${room.turnPoints + (sel.valid ? sel.points : 0)})`;
  bankBtn.disabled = !isMyTurn || room.phase !== 'selecting' || (room.turnPoints === 0 && (!sel.valid || sel.points === 0));
  bankBtn.addEventListener('click', () => ws.send({ type: 'bank' }));
  actions.append(rollBtn, bankBtn);
  wrap.appendChild(actions);

  if (room.hotDiceAvailable) {
    const hot = document.createElement('div');
    hot.className = 'hot-dice-banner';
    hot.textContent = 'Hot Dice!';
    wrap.appendChild(hot);
  }

  if (room.phase === 'between-turns' && room.lastBust) {
    const bust = document.createElement('div');
    bust.className = 'bust-banner';
    if (isFirstRoll) bust.classList.add('intro');
    bust.innerHTML = `
      <div class="bust-line">BUST!</div>
      <div class="bust-detail">${escapeHtml(room.lastBust.playerName)} verliert ${room.lastBust.lostPoints} Punkte.</div>
    `;
    wrap.appendChild(bust);
  }
  return wrap;
}

function renderFinishedArea(room: RoomState, me: Player | null, ws: WSClient): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'phase-area finished';
  const winner = room.players.find(p => p.id === room.winnerId);
  const headline = document.createElement('h2');
  headline.className = 'winner-headline';
  headline.textContent = winner ? `${winner.name} gewinnt!` : 'Spiel beendet.';
  wrap.appendChild(headline);

  const scores = document.createElement('ol');
  scores.className = 'final-scores';
  for (const p of [...room.players].sort((a, b) => b.score - a.score)) {
    const li = document.createElement('li');
    li.innerHTML = `<span>${escapeHtml(p.name)}</span><strong>${p.score}</strong>`;
    scores.appendChild(li);
  }
  wrap.appendChild(scores);

  if (me?.isHost) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'primary big';
    btn.textContent = 'Nochmal';
    btn.addEventListener('click', () => ws.send({ type: 'rematch' }));
    wrap.appendChild(btn);
  }
  return wrap;
}

function renderRulesCheatSheet(): HTMLElement {
  const det = document.createElement('details');
  det.className = 'rules';
  det.open = rulesOpen;
  det.addEventListener('toggle', () => { rulesOpen = det.open; });
  const sum = document.createElement('summary');
  sum.textContent = 'Wertungstabelle';
  det.appendChild(sum);
  const rows: [string, string][] = [
    ['Einzelne 1', String(SINGLE_ONE)],
    ['Einzelne 5', String(SINGLE_FIVE)],
    ['Drei 1er', '1000'],
    ['Drei 2er / 3er / 4er / 5er / 6er', '200 / 300 / 400 / 500 / 600'],
    ['Vier gleiche', '2× Drilling'],
    ['Fünf gleiche', '4× Drilling'],
    ['Sechs gleiche', '8× Drilling'],
    ['Straße 1–5', String(STRAIGHT_1_5)],
    ['Straße 2–6', String(STRAIGHT_2_6)],
    ['Volle Straße 1–6', String(STRAIGHT_FULL)],
  ];
  const table = document.createElement('table');
  for (const [k, v] of rows) {
    const tr = document.createElement('tr');
    tr.innerHTML = `<td>${k}</td><td>${v}</td>`;
    table.appendChild(tr);
  }
  det.appendChild(table);
  return det;
}

function renderLog(room: RoomState): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'log';
  const recent = room.log.slice(-10).reverse();
  for (const e of recent) {
    const div = document.createElement('div');
    div.className = 'log-entry';
    div.textContent = e.msg;
    wrap.appendChild(div);
  }
  wrap.addEventListener('scroll', () => { logScrollTop = wrap.scrollTop; });
  queueMicrotask(() => { wrap.scrollTop = logScrollTop; });
  return wrap;
}

function renderToast(host: HTMLElement, s: ClientState): void {
  if (!s.lastError) { host.classList.remove('show'); host.textContent = ''; return; }
  if (Date.now() - s.lastError.ts > 4000) { host.classList.remove('show'); host.textContent = ''; return; }
  host.textContent = ERR_LABELS[s.lastError.code] ?? s.lastError.msg;
  host.classList.add('show');
  window.setTimeout(() => {
    if (host.classList.contains('show')) {
      host.classList.remove('show');
      host.textContent = '';
    }
  }, 4000);
}

function copyRoomCode(code: string, el: HTMLElement): void {
  const label = el.querySelector('.label');
  const flash = (): void => {
    if (!label) return;
    const orig = label.textContent;
    label.textContent = 'Kopiert!';
    el.classList.add('copied');
    window.setTimeout(() => {
      if (label.textContent === 'Kopiert!') label.textContent = orig;
      el.classList.remove('copied');
    }, 1200);
  };
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(code).then(flash).catch(() => fallbackCopy(code, flash));
  } else {
    fallbackCopy(code, flash);
  }
}

function fallbackCopy(text: string, done: () => void): void {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  try { document.execCommand('copy'); done(); } catch { /* ignore */ }
  ta.remove();
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
